/**
 * Analytics API Service  
 * Handles Flask Backend API (lw-backend-container) requests for:
 * - Freight data queries
 * - Broker metrics
 * - Rate insights
 * - Lane pricing analysis
 */
import BaseApiService from './BaseApiService';
import { getApiUrl } from '../../config/environment';

class AnalyticsApiService extends BaseApiService {
  constructor() {
    super('AnalyticsAPI', getApiUrl('analytics'));
  }
  
  
  /**
   * Get freight data with filters
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} - Standardized API response with freight data
   */
  async getFreightData(filters = {}) {
    try {
      console.log(`[${this.serviceName}] Getting freight data with filters:`, filters);
      
      const params = {
        pickup_date_begin: filters.pickupDateBegin,
        pickup_date_end: filters.pickupDateEnd,
        equipment_types: filters.equipmentTypes,
        origin_states: filters.originStates,
        destination_states: filters.destinationStates,
        min_payment: filters.minPayment || 0,
        min_rate_per_mile: filters.minRatePerMile || 0,
        limit: filters.limit || 1000
      };

      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });

      const response = await this.get('/freight-data', params);
      
      console.log(`[${this.serviceName}] Freight data retrieved - ${response.data?.length || 0} records`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Get freight data failed:`, error);
      throw error;
    }
  }
  
  /**
   * Get broker metrics
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} - Standardized API response with broker metrics
   */
  async getBrokerMetrics(filters = {}) {
    try {
      console.log(`[${this.serviceName}] Getting broker metrics with filters:`, filters);
      
      const filterData = {
        pickup_date_begin: filters.pickupDateBegin,
        pickup_date_end: filters.pickupDateEnd,
        equipment_types: filters.equipmentTypes,
        load_types: filters.loadTypes,
        origin_states: filters.originStates,
        destination_states: filters.destinationStates,
        min_payment: filters.minPayment || 0,
        min_rate_per_mile: filters.minRatePerMile || 0,
        max_length: filters.maxLength || 75,
        max_weight: filters.maxWeight || 100000,
        limit: filters.limit || 1000
      };

      // Remove undefined values
      Object.keys(filterData).forEach(key => {
        if (filterData[key] === undefined || filterData[key] === null) {
          delete filterData[key];
        }
      });

      const response = await this.post('/broker-metrics', filterData);
      
      console.log(`[${this.serviceName}] Broker metrics retrieved successfully`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Get broker metrics failed:`, error);
      throw error;
    }
  }
  
  /**
   * Get rate insights
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} - Standardized API response with rate insights
   */
  async getRateInsights(filters = {}) {
    try {
      console.log(`[${this.serviceName}] Getting rate insights with filters:`, filters);
      
      const filterData = {
        pickup_date_begin: filters.pickupDateBegin,
        pickup_date_end: filters.pickupDateEnd,
        equipment_types: filters.equipmentTypes,
        load_types: filters.loadTypes,
        origin_states: filters.originStates,
        destination_states: filters.destinationStates,
        min_payment: filters.minPayment || 0,
        min_rate_per_mile: filters.minRatePerMile || 0,
        max_length: filters.maxLength || 75,
        max_weight: filters.maxWeight || 100000,
        limit: filters.limit || 1000
      };

      // Remove undefined values
      Object.keys(filterData).forEach(key => {
        if (filterData[key] === undefined || filterData[key] === null) {
          delete filterData[key];
        }
      });

      const response = await this.post('/rate-insights', filterData);
      
      console.log(`[${this.serviceName}] Rate insights retrieved successfully`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Get rate insights failed:`, error);
      throw error;
    }
  }
  
  /**
   * Get lane pricing analysis
   * @param {Object} laneData - Lane parameters
   * @returns {Promise<Object>} - Standardized API response with lane pricing data
   */
  async getLanePricing(laneData) {
    try {
      console.log(`[${this.serviceName}] Getting lane pricing for:`, laneData);
      
      const requestData = {
        origin_city: laneData.originCity,
        origin_state: laneData.originState,
        destination_city: laneData.destinationCity,
        destination_state: laneData.destinationState,
        equipment_type: laneData.equipmentType
      };

      const response = await this.post('/lane-pricing', requestData);
      
      // Check if the response contains an error message
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
      
      console.log(`[${this.serviceName}] Lane pricing retrieved - Sample size: ${response.data?.sample_size}, Supporting loads: ${response.data?.supporting_loads?.length || 0}`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Get lane pricing failed:`, error);
      throw error;
    }
  }
  
  /**
   * Get route optimization analysis
   * @param {Object} routeData - Route optimization parameters
   * @returns {Promise<Object>} - Standardized API response with optimized route plan
   */
  async getRouteOptimization(routeData) {
    try {
      console.log(`[${this.serviceName}] Getting route optimization for:`, routeData);
      
      // Parse city and state from combined origin field
      const originParts = (routeData.origin || '').split(',').map(part => part.trim());
      const originCity = originParts[0] || '';
      const originState = originParts[1] || '';
      
      const requestData = {
        origin_city: originCity,
        origin_state: originState,
        equipment_type: routeData.equipment_type,
        radius: routeData.radius,
        max_trips: routeData.max_trips,
        cost_per_mile: routeData.cost_per_mile || 1.85,
        min_mileage: routeData.min_mileage || 100
      };

      const response = await this.post('/route-optimization', requestData);
      
      // Check if the response contains an error message
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
      
      console.log(`[${this.serviceName}] Route optimization retrieved - ${response.data?.summary?.total_trips || 0} trips planned`);
      return response;
      
    } catch (error) {
      console.error(`[${this.serviceName}] Get route optimization failed:`, error);
      throw error;
    }
  }
  
  /**
   * Get health status (internal endpoint)
   * @returns {Promise<Object>} - Health status response
   */
  async getHealth() {
    try {
      const response = await this.get('/health');
      console.log(`[${this.serviceName}] Health check passed`);
      return response;
    } catch (error) {
      console.error(`[${this.serviceName}] Health check failed:`, error);
      throw error;
    }
  }
}

// Create and export singleton instance
export default new AnalyticsApiService();